import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatIDR, formatDateID } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { BadgeDollarSign, Search, Pencil, Plus, Trash2, RefreshCw } from "lucide-react";

const emptyProfile = () => ({
  name: "",
  phone: "",
  email: "",
  address: "",
  note: "",
});

export default function Sales() {
  const [salesList, setSalesList] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(null);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/sales");
      setSalesList(r.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const syncFromInvoices = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/sales/sync-from-invoices");
      const { scanned = 0, created = 0 } = r.data || {};
      if (created > 0) {
        toast.success(`${created} sales baru ditambahkan (dari ${scanned} nama unik)`);
      } else {
        toast.info(`Semua ${scanned} sales sudah punya profil.`);
      }
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal sinkronisasi");
    } finally {
      setSyncing(false);
    }
  };

  const openCreate = () => setCreating(emptyProfile());

  const openEdit = (c) => {
    setEditing({
      originalName: c.name,
      name: c.name,
      phone: c.phone || "",
      email: c.email || "",
      address: c.address || "",
      note: c.note || "",
    });
  };

  const saveNew = async (e) => {
    e.preventDefault();
    if (!creating.name.trim()) {
      toast.error("Nama sales wajib diisi");
      return;
    }
    setSaving(true);
    try {
      await api.post("/sales", {
        name: creating.name.trim(),
        phone: creating.phone.trim(),
        email: creating.email.trim(),
        address: creating.address.trim(),
        note: creating.note.trim(),
      });
      toast.success("Sales ditambahkan");
      setCreating(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menambahkan sales");
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editing.name.trim()) {
      toast.error("Nama sales wajib diisi");
      return;
    }
    setSaving(true);
    try {
      const r = await api.patch(
        `/sales/${encodeURIComponent(editing.originalName)}`,
        {
          name: editing.name.trim(),
          phone: editing.phone.trim(),
          email: editing.email.trim(),
          address: editing.address.trim(),
          note: editing.note.trim(),
        },
      );
      const updated = r.data?.updated ?? 0;
      toast.success(
        updated > 0
          ? `Profil disimpan · ${updated} faktur diperbarui`
          : "Profil sales disimpan",
      );
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal memperbarui sales");
    } finally {
      setSaving(false);
    }
  };

  const deleteProfile = async (name) => {
    if (!window.confirm(`Hapus profil sales "${name}"?\n(Faktur tetap tersimpan)`)) return;
    try {
      await api.delete(`/sales/${encodeURIComponent(name)}`);
      toast.success("Profil sales dihapus");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menghapus");
    }
  };

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return salesList;
    return salesList.filter((c) =>
      [c.name, c.phone, c.email, c.address, c.note]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(s)),
    );
  }, [salesList, q]);

  const totalSales = salesList.length;
  const totalRevenue = salesList.reduce((s, c) => s + Number(c.total_amount || 0), 0);

  return (
    <div className="space-y-6" data-testid="sales-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-zinc-900 text-white">
            <BadgeDollarSign className="h-4 w-4" />
          </span>
          <div>
            <h1 className="text-2xl sm:text-3xl font-display font-bold text-zinc-900">Data Sales</h1>
            <p className="text-sm text-zinc-500 mt-1">
              Kelola profil sales/marketing lengkap dengan No HP, email & catatan.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            data-testid="btn-sync-sales"
            onClick={syncFromInvoices}
            disabled={syncing}
            variant="outline"
            className="rounded-md"
            title="Import semua nama sales dari faktur"
          >
            <RefreshCw className={`h-4 w-4 mr-1.5 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Sinkron…" : "Sinkron dari Faktur"}
          </Button>
          <Button
            data-testid="btn-add-sales"
            onClick={openCreate}
            size="lg"
            className="bg-zinc-900 hover:bg-zinc-800 shadow-sm"
          >
            <Plus className="h-4 w-4 mr-1.5" />
            Data Sales
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card className="p-4 border-zinc-200 shadow-none rounded-md">
          <div className="text-xs text-zinc-500 uppercase tracking-wider">Jumlah Sales</div>
          <div className="mt-1 text-xl font-display font-bold num" data-testid="sales-total-count">
            {totalSales}
          </div>
        </Card>
        <Card className="p-4 border-zinc-200 shadow-none rounded-md">
          <div className="text-xs text-zinc-500 uppercase tracking-wider">Total Omset Semua Sales</div>
          <div className="mt-1 text-xl font-display font-bold num text-emerald-700" data-testid="sales-total-amount">
            {formatIDR(totalRevenue)}
          </div>
        </Card>
      </div>

      <Card className="border-zinc-200 shadow-none rounded-md">
        <div className="p-4 border-b border-zinc-200 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              data-testid="sales-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cari nama, HP, email…"
              className="pl-9 h-10 rounded-md"
            />
          </div>
          <div className="text-xs text-zinc-500">
            {filtered.length} dari {totalSales} sales
          </div>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="bg-zinc-50">
              <TableRow>
                <TableHead className="min-w-[180px]">Nama Sales</TableHead>
                <TableHead className="hidden md:table-cell w-[150px]">No HP</TableHead>
                <TableHead className="hidden lg:table-cell w-[200px]">Email</TableHead>
                <TableHead className="hidden xl:table-cell min-w-[200px]">Alamat / Catatan</TableHead>
                <TableHead className="text-right w-[90px]">Faktur</TableHead>
                <TableHead className="text-right w-[160px]">Total Omset</TableHead>
                <TableHead className="hidden lg:table-cell w-[160px]">Faktur Terakhir</TableHead>
                <TableHead className="text-right w-[110px]">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody data-testid="sales-table-body">
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-10 text-zinc-500">
                    Memuat…
                  </TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-10 text-zinc-500">
                    Belum ada sales. Klik "Data Sales" untuk mulai menambah.
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((c) => (
                  <TableRow
                    key={c.name}
                    className="hover:bg-zinc-50 transition-colors"
                    data-testid={`sales-row-${c.name.replace(/\s+/g, "_")}`}
                  >
                    <TableCell className="font-medium">
                      <div>{c.name}</div>
                      {c.note && (
                        <div className="text-[11px] text-zinc-500 italic mt-0.5 max-w-[220px] truncate" title={c.note}>
                          {c.note}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-zinc-700 font-mono text-xs">
                      {c.phone || <span className="text-zinc-400">—</span>}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-zinc-700 text-xs">
                      {c.email || <span className="text-zinc-400">—</span>}
                    </TableCell>
                    <TableCell className="hidden xl:table-cell text-zinc-600 max-w-[280px]">
                      {c.address || <span className="text-zinc-400 italic">—</span>}
                    </TableCell>
                    <TableCell className="text-right num">{c.invoice_count || 0}</TableCell>
                    <TableCell className="text-right num font-semibold text-emerald-700">
                      {formatIDR(c.total_amount || 0)}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-zinc-600">
                      {c.last_date ? (
                        <>
                          <div>{formatDateID(c.last_date)}</div>
                          {c.last_invoice && (
                            <div className="text-[11px] text-zinc-400 font-mono">{c.last_invoice}</div>
                          )}
                        </>
                      ) : (
                        <span className="text-zinc-400">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          data-testid={`btn-edit-sales-${c.name.replace(/\s+/g, "_")}`}
                          size="icon"
                          variant="ghost"
                          onClick={() => openEdit(c)}
                          className="h-8 w-8"
                          title="Edit"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        {(c.invoice_count || 0) === 0 && (
                          <Button
                            data-testid={`btn-delete-sales-${c.name.replace(/\s+/g, "_")}`}
                            size="icon"
                            variant="ghost"
                            onClick={() => deleteProfile(c.name)}
                            className="h-8 w-8 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                            title="Hapus profil"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Create dialog */}
      <Dialog open={!!creating} onOpenChange={(o) => !o && setCreating(null)}>
        <DialogContent data-testid="create-sales-dialog" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Tambah Sales Baru</DialogTitle>
            <DialogDescription>
              Data sales akan otomatis muncul di form faktur penjualan.
            </DialogDescription>
          </DialogHeader>
          {creating && (
            <form onSubmit={saveNew} className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Nama Sales <span className="text-rose-600">*</span></Label>
                <Input
                  data-testid="create-sales-name"
                  required
                  autoFocus
                  value={creating.name}
                  onChange={(e) => setCreating({ ...creating, name: e.target.value })}
                />
              </div>
              <div>
                <Label>No HP / Telepon</Label>
                <Input
                  data-testid="create-sales-phone"
                  placeholder="0812xxxxxxxx"
                  value={creating.phone}
                  onChange={(e) => setCreating({ ...creating, phone: e.target.value })}
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  data-testid="create-sales-email"
                  type="email"
                  placeholder="sales@email.com"
                  value={creating.email}
                  onChange={(e) => setCreating({ ...creating, email: e.target.value })}
                />
              </div>
              <div className="col-span-2">
                <Label>Alamat</Label>
                <Input
                  data-testid="create-sales-address"
                  placeholder="Alamat rumah / kantor"
                  value={creating.address}
                  onChange={(e) => setCreating({ ...creating, address: e.target.value })}
                />
              </div>
              <div className="col-span-2">
                <Label>Catatan</Label>
                <Input
                  data-testid="create-sales-note"
                  placeholder="Catatan internal (opsional)"
                  value={creating.note}
                  onChange={(e) => setCreating({ ...creating, note: e.target.value })}
                />
              </div>
              <DialogFooter className="col-span-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setCreating(null)}>
                  Batal
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  data-testid="btn-save-new-sales"
                  className="bg-zinc-900 hover:bg-zinc-800"
                >
                  {saving ? "Menyimpan…" : "Simpan Sales"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="edit-sales-dialog" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Sales</DialogTitle>
            <DialogDescription>
              Perubahan nama akan berlaku untuk semua faktur sales ini.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <form onSubmit={saveEdit} className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Nama Sales <span className="text-rose-600">*</span></Label>
                <Input
                  data-testid="edit-sales-name"
                  required
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                />
              </div>
              <div>
                <Label>No HP / Telepon</Label>
                <Input
                  data-testid="edit-sales-phone"
                  placeholder="0812xxxxxxxx"
                  value={editing.phone}
                  onChange={(e) => setEditing({ ...editing, phone: e.target.value })}
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  data-testid="edit-sales-email"
                  type="email"
                  placeholder="sales@email.com"
                  value={editing.email}
                  onChange={(e) => setEditing({ ...editing, email: e.target.value })}
                />
              </div>
              <div className="col-span-2">
                <Label>Alamat</Label>
                <Input
                  data-testid="edit-sales-address"
                  placeholder="Alamat rumah / kantor"
                  value={editing.address}
                  onChange={(e) => setEditing({ ...editing, address: e.target.value })}
                />
              </div>
              <div className="col-span-2">
                <Label>Catatan</Label>
                <Input
                  data-testid="edit-sales-note"
                  placeholder="Catatan internal (opsional)"
                  value={editing.note}
                  onChange={(e) => setEditing({ ...editing, note: e.target.value })}
                />
              </div>
              <DialogFooter className="col-span-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setEditing(null)}>
                  Batal
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  data-testid="btn-save-sales"
                  className="bg-zinc-900 hover:bg-zinc-800"
                >
                  {saving ? "Menyimpan…" : "Simpan Perubahan"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
